/**
 * FuelPriceChart - 30-day price history SVG chart
 */

import React, { useMemo } from "react";

export function FuelPriceChart({ priceHistory, currentPrice, hedges = [], height = 120 }) {
  const data = priceHistory || [];
  const width = 100; // Percentage based

  const chartData = useMemo(() => {
    if (data.length === 0) return { points: "", min: 2.5, max: 3.5 };

    const prices = data.map((d) => d.price);
    const min = Math.min(...prices) * 0.95;
    const max = Math.max(...prices) * 1.05;
    const range = max - min || 0.1;

    // Generate SVG path points
    const points = data
      .map((d, i) => {
        const x = (i / (data.length - 1 || 1)) * 100;
        const y = 100 - ((d.price - min) / range) * 100;
        return `${x},${y}`;
      })
      .join(" ");

    return { points, min, max, range };
  }, [data]);

  // Current price position
  const currentY = useMemo(() => {
    if (!currentPrice || !chartData.range) return 50;
    return 100 - ((currentPrice - chartData.min) / chartData.range) * 100;
  }, [currentPrice, chartData]);

  // Hedge break-even lines
  const hedgeLines = useMemo(() => {
    return hedges
      .filter((h) => h.status === "active" || h.status === "pending")
      .map((h) => ({
        price: h.breakEvenPrice,
        y: 100 - ((h.breakEvenPrice - chartData.min) / chartData.range) * 100,
        label: `BE: $${h.breakEvenPrice.toFixed(2)}`,
      }));
  }, [hedges, chartData]);

  if (data.length < 2) {
    return (
      <div
        style={{
          height,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "#111",
          borderRadius: "8px",
          border: "1px solid #1a1a1a",
          color: "#525252",
          fontSize: "12px",
        }}
      >
        Price history will appear after a few days
      </div>
    );
  }

  return (
    <div
      style={{
        position: "relative",
        height,
        backgroundColor: "#111",
        borderRadius: "8px",
        border: "1px solid #1a1a1a",
        padding: "8px",
      }}
    >
      {/* Y-axis labels */}
      <div
        style={{
          position: "absolute",
          left: "4px",
          top: "8px",
          fontSize: "10px",
          color: "#525252",
        }}
      >
        ${chartData.max.toFixed(2)}
      </div>
      <div
        style={{
          position: "absolute",
          left: "4px",
          bottom: "8px",
          fontSize: "10px",
          color: "#525252",
        }}
      >
        ${chartData.min.toFixed(2)}
      </div>

      {/* SVG Chart */}
      <svg
        viewBox="0 0 100 100"
        preserveAspectRatio="none"
        style={{
          position: "absolute",
          left: "40px",
          right: "8px",
          top: "8px",
          bottom: "8px",
          width: "calc(100% - 48px)",
          height: `calc(100% - 16px)`,
        }}
      >
        {/* Grid lines */}
        <line
          x1="0"
          y1="25"
          x2="100"
          y2="25"
          stroke="#1a1a1a"
          strokeWidth="0.5"
        />
        <line
          x1="0"
          y1="50"
          x2="100"
          y2="50"
          stroke="#1a1a1a"
          strokeWidth="0.5"
        />
        <line
          x1="0"
          y1="75"
          x2="100"
          y2="75"
          stroke="#1a1a1a"
          strokeWidth="0.5"
        />

        {/* Hedge break-even lines */}
        {hedgeLines.map((line, i) => (
          <g key={i}>
            <line
              x1="0"
              y1={line.y}
              x2="100"
              y2={line.y}
              stroke="#eab308"
              strokeWidth="0.5"
              strokeDasharray="2,2"
            />
          </g>
        ))}

        {/* Price line */}
        <polyline
          fill="none"
          stroke="#60ffd0"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          points={chartData.points}
        />

        {/* Current price dot */}
        <circle
          cx="100"
          cy={currentY}
          r="3"
          fill="#60ffd0"
          stroke="#0c0c0c"
          strokeWidth="1"
        />
      </svg>

      {/* Legend */}
      <div
        style={{
          position: "absolute",
          bottom: "4px",
          right: "8px",
          display: "flex",
          gap: "12px",
          fontSize: "10px",
        }}
      >
        <span style={{ color: "#525252" }}>30 days</span>
        {hedgeLines.length > 0 && (
          <span style={{ color: "#eab308" }}>— Hedge BE</span>
        )}
      </div>
    </div>
  );
}

export default FuelPriceChart;
